(() => {
  if (customElements.get('adk-perfume-set')) return;
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const EASE = 'cubic-bezier(.22,1,.36,1)', SPRING = 'cubic-bezier(.34,1.56,.64,1)';
  const WORDS = {2:'two', 3:'three', 4:'four', 5:'five'};
  class ADKPerfumeSet extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      try { this.cfg = JSON.parse(this.querySelector('[data-apb-config]').textContent); }
      catch { this.ready = false; return; }
      this.cfg.tiers = this.cfg.tiers || {2:5,3:8,4:10,5:15};
      this.cards = [...this.querySelectorAll('[data-apb-card]')];
      this.products = this.cards.map(card => { const p = JSON.parse(card.querySelector('[data-apb-product]').textContent); p.image = p.image || card.querySelector('img')?.src; p.card = card; return p; });
      this.byId = new Map(this.products.map(p => [String(p.id), p]));
      this.key = `adk-perfume-set-v2:${this.id}:${this.dataset.storeKey || location.pathname}:${this.cfg.currency}`;
      this.selected = [];
      this.busy = false;
      this.first = true;
      this.prevN = 0;
      this.prevPercent = 0;
      // Set size mode (product-page widget only): a fixed set of 2–5 chosen on the product page. The page never sets data-target.
      // data-max (widget only) caps the set size, e.g. 4; without it the cap stays 5.
      this.target = this.hasAttribute('data-target') ? Math.min(this.maxSize, Math.max(2, Number(this.read('target')) || Number(this.dataset.target) || 2)) : 0;
      if (this.target) this.dataset.target = String(this.target);
      this.pending = this.read('pending');
      const saved = this.read('selection');
      if (Array.isArray(saved)) for (const id of saved) {
        const p = this.byId.get(String(id));
        if (this.selected.length < (this.target || 5) && p?.available && this.quantity(p.id) < p.max) this.selected.push(String(id));
      }
      this.dialog = this.querySelector('dialog');
      this.dock = this.querySelector('[data-apb-dock]');
      this.outline = this.querySelector('[data-apb-outline]').innerHTML;
      this.slots = [...this.querySelectorAll('.apb-slot')];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.animations = new Set();
      this.timers = new Set();
      this.flyers = new Set();
      this.addEventListener('click', event => this.click(event), {signal:this.abort.signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-apb-filter]')) this.filter(event.target.value);
      }, {signal:this.abort.signal});
      this.dialog.addEventListener('close', () => this.closed(), {signal:this.abort.signal});
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDialog(); }, {signal:this.abort.signal});
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog();
        }
      }, {signal:this.abort.signal});
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target === this) this.onScreen = entry.isIntersecting;
          else this.trayVisible = entry.isIntersecting;
        }
        this.toggleDock(!!this.onScreen);
      }, {threshold:0});
      this.observer.observe(this);
      this.observer.observe(this.querySelector('[data-apb-tray-cta]'));
      if ('ResizeObserver' in window) { this.resizer = new ResizeObserver(() => this.measureDock()); this.resizer.observe(this.dock); }
      this.querySelector('[data-apb-nojs]').hidden = true;
      if (this.target) this.tierButtons();
      this.update();
      this.first = false;
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.resizer?.disconnect();
      clearTimeout(this.toastTimer); clearTimeout(this.dockTimer); clearTimeout(this.closeTimer);
      this.timers?.forEach(clearTimeout); this.timers?.clear();
      this.flyers?.forEach(node => node.remove()); this.flyers?.clear();
      if (this.dialog?.open) this.dialog.close();
      if (this.oldOverflow !== undefined) document.body.style.overflow = this.oldOverflow;
      this.animations?.forEach(a => a.cancel());
      this.ready = false;
    }
    get maxSize() { return Math.min(5, Math.max(2, Number(this.dataset.max) || 5)); }
    get limit() { return this.target || 5; }
    get goal() { return this.target || 2; }
    tierButtons() {
      this.querySelectorAll('[data-apb-tier]').forEach(el => {
        el.setAttribute('role', 'button'); el.tabIndex = 0;
        el.setAttribute('aria-label', `Set of ${el.getAttribute('data-apb-tier')}`);
        el.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.setTarget(el.getAttribute('data-apb-tier')); } }, {signal:this.abort.signal});
      });
    }
    setTarget(value) {
      if (!this.target) return;
      const n = Math.min(this.maxSize, Math.max(2, Number(value) || 2));
      const trimmed = this.selected.length > n;
      if (trimmed) this.selected = this.selected.slice(0, n);
      const changed = n !== this.target;
      this.target = n; this.dataset.target = String(n); this.write('target', n);
      this.slots.forEach(slot => { if (!slot.dataset.id) slot.dataset.id = '-'; });
      this.update();
      if (trimmed) this.toast(`Kept your first ${WORDS[n]}. Tap a bigger set to add more.`);
      if (changed) { this.announce(`Set of ${n} selected.`); this.dispatchEvent(new CustomEvent('adk:set-target', {bubbles:true, detail:{target:n}})); }
    }
    estimateTo(size) {
      const percent = Number(this.cfg.tiers[size] || 0);
      const pool = this.products.filter(p => p.available);
      const unit = pool.length ? Math.min(...pool.map(p => p.price)) : 0;
      const prices = [...this.selected.map(id => this.byId.get(id).price), ...Array(Math.max(0, size - this.selected.length)).fill(unit)];
      return prices.reduce((sum, price) => sum + Math.round(price * percent / 100), 0);
    }
    targetNudge({n, percent, saving}) {
      const goal = this.target, pct = this.cfg.tiers[goal], m = value => this.money(value), left = goal - n;
      if (n >= goal) return {state:'best', badge:'✓', title:`${percent}% off unlocked`, detail:`You're saving ${m(saving)} on your ${WORDS[goal]}.`, dock:`<b>✓ ${percent}% off</b><span class="apb-dock-extra"> · you save ${m(saving)}</span>`};
      return {state:n ? 'progress' : 'start', badge:`${pct}%`, title:n ? `${left} more for ${pct}% off` : `Pick ${goal} scents for ${pct}% off`, detail:n ? `That takes ${m(this.estimateTo(goal))} off your set.` : 'Mix freely. Repeats welcome.', dock:`<b>${left} more</b> for ${pct}% off`};
    }
    read(suffix) { try { return JSON.parse(sessionStorage.getItem(`${this.key}:${suffix}`)); } catch { return null; } }
    write(suffix, value) { try { value === null ? sessionStorage.removeItem(`${this.key}:${suffix}`) : sessionStorage.setItem(`${this.key}:${suffix}`, JSON.stringify(value)); } catch {} }
    money(cents) { return new Intl.NumberFormat(this.cfg.locale || 'en-US', {style:'currency',currency:this.cfg.currency || 'USD',maximumFractionDigits:cents % 100 ? 2 : 0}).format(cents / 100); }
    quantity(id) { return this.selected.filter(value => value === String(id)).length; }
    later(fn, ms) { const t = setTimeout(() => { this.timers.delete(t); fn(); }, ms); this.timers.add(t); return t; }
    click(event) {
      const tier = this.target && !this.busy && event.target.closest('[data-apb-tier]');
      if (tier && this.contains(tier)) { this.setTarget(tier.getAttribute('data-apb-tier')); return; }
      const button = event.target.closest('[data-apb-action]');
      if (!button || !this.contains(button)) return;
      const action = button.dataset.apbAction, id = button.dataset.id;
      if (action === 'close') { this.closeDialog(); return; }
      if (action === 'ingredients') { this.toggleIngredients(button); return; }
      if (this.busy) return;
      if (action === 'add') { const card = button.closest('[data-apb-card]'); this.add(id, card); if (card && (button.hidden || button.disabled)) card.querySelector('[data-apb-stepper] button:not(:disabled)')?.focus({preventScroll:true}); }
      if (action === 'minus') { this.remove(this.selected.lastIndexOf(String(id))); if (button.closest('[data-apb-stepper]').hidden) button.closest('[data-apb-card]').querySelector('[data-apb-add]').focus({preventScroll:true}); }
      if (action === 'remove') this.remove(Number(button.dataset.slot), true);
      if (action === 'clear') this.clear();
      if (action === 'detail') this.open('detail', id, button);
      if (action === 'review') this.open('review', null, button);
      if (action === 'cart') this.submit();
    }
    add(id, card = null) {
      const p = this.byId.get(String(id));
      if (!p?.available) return;
      if (this.selected.length >= this.limit) { this.toast(this.target ? `Your ${WORDS[this.target]} are chosen. Remove one, or tap a bigger set.` : 'Your five are chosen. Remove one to make room for another.'); return; }
      if (this.quantity(id) >= p.max) { this.toast(`No more ${p.title} perfumes are available.`); return; }
      this.selected.push(String(id));
      const slot = this.selected.length - 1;
      const flight = card && !this.dialog.open ? this.fly(p, slot) : null;
      const {tierUp, price} = this.update({added:slot, flight});
      this.announce(`${p.title} added. ${this.selected.length} of ${this.limit} chosen.${tierUp ? ` ${price.percent}% off unlocked.` : ''}`);
    }
    remove(index, restoreFocus = false) {
      if (index < 0 || index >= this.selected.length) return;
      const p = this.byId.get(this.selected[index]);
      const wasDialog = this.dialog.contains(document.activeElement);
      this.selected.splice(index, 1);
      this.update({removed:index});
      this.announce(`${p.title} removed. ${this.selected.length} of ${this.limit} chosen.`);
      if (restoreFocus) {
        const scope = wasDialog ? this.dialog : this.querySelector('[data-apb-slots]');
        const next = scope.querySelectorAll('[data-apb-action=remove]');
        (next[Math.min(index,next.length-1)] || (wasDialog ? this.dialog.querySelector('[data-apb-action=close]') : this.querySelector('[data-apb-filter]'))).focus({preventScroll:true});
      }
    }
    clear() {
      if (this.busy || !this.selected.length) return;
      const inReview = this.dialog.open && this.view === 'review';
      this.selected = [];
      this.lastError = '';
      this.querySelector('[data-apb-error]').hidden = true;
      clearTimeout(this.toastTimer);
      const toast = this.querySelector('[data-apb-toast]'); toast.hidden = true; toast.classList.remove('is-leaving');
      this.update({cleared:true});
      this.announce(this.target ? `Your set has been cleared. Choose ${this.target} perfumes to start again.` : 'Your set has been cleared. Choose 2–5 perfumes to start again.');
      (inReview ? this.dialog.querySelector('[data-apb-action=close]') : this.querySelector('[data-apb-filter]')).focus({preventScroll:true});
    }
    totalSeparate() { return this.selected.reduce((sum,id) => sum + this.byId.get(id).price,0); }
    pricing() {
      const n=this.selected.length, percent=n>=2?Number(this.cfg.tiers[n] || 0):0;
      const subtotal=this.totalSeparate();
      const saving=this.selected.reduce((sum,id)=>sum+Math.round(this.byId.get(id).price*percent/100),0);
      return {n,percent,subtotal,saving,total:subtotal-saving};
    }
    estimate(size) {
      const percent = Number(this.cfg.tiers[size] || 0);
      const pool = this.products.filter(p => p.available && this.quantity(p.id) < p.max);
      const unit = pool.length ? Math.min(...pool.map(p => p.price)) : 0;
      return [...this.selected.map(id => this.byId.get(id).price), unit].reduce((sum, price) => sum + Math.round(price * percent / 100), 0);
    }
    nudge(price = this.pricing()) {
      if (this.target) return this.targetNudge(price);
      const {n, percent, saving} = price, t = this.cfg.tiers, m = value => this.money(value);
      if (n === 0) return {state:'start', badge:`${t[2]}%`, title:`Pick any 2 scents to unlock ${t[2]}% off`, detail:'Mix freely. Repeats welcome.', dock:`Pick 2<span class="apb-dock-extra"> scents</span> to unlock <b>${t[2]}% off</b>`};
      if (n >= 5) return {state:'best', badge:'✓', title:`Best value unlocked: ${percent}% off`, detail:`You're saving ${m(saving)} on all five.`, dock:`<b>✓ Best value</b> · ${percent}% off<span class="apb-dock-extra"> all five</span>`};
      if (n === 1) return {state:'progress', badge:`${t[2]}%`, title:`1 more scent unlocks ${t[2]}% off`, detail:`That takes ${m(this.estimate(2))} off your set.`, dock:`<b>1 more</b> unlocks ${t[2]}% off`};
      const next = n + 1, est = this.estimate(next);
      return {state:'progress', badge:`${t[next]}%`, title:`Add 1 more to unlock ${t[next]}% off`, detail:`${next === 5 ? 'Our best value. ' : ''}Save ${m(est)} instead of ${m(saving)}.`, dock:`<b>✓ ${percent}% off</b> · 1 more → ${t[next]}%`};
    }
    update({added = -1, flight = null, removed = -1, cleared = false} = {}) {
      const n = this.selected.length, price = this.pricing(), nudge = this.nudge(price);
      const live = !this.first;
      const tierUp = live && n >= 2 && price.percent > this.prevPercent && (!this.target || n === this.target);
      const becameReady = live && n >= this.goal && this.prevN < this.goal;
      this.cards.forEach((card,index) => {
        const p = this.products[index], count = this.quantity(p.id), before = card.apbCount ?? count;
        card.apbCount = count;
        const addButton = card.querySelector('[data-apb-add]'), stepper = card.querySelector('[data-apb-stepper]'), output = card.querySelector('output'), badge = card.querySelector('[data-apb-badge]');
        card.classList.toggle('is-selected',count > 0);
        addButton.hidden = count > 0;
        stepper.hidden = count === 0;
        output.textContent = String(count);
        badge.textContent = String(count); badge.hidden = count === 0;
        card.querySelectorAll('[data-apb-action=add]').forEach(b => { b.disabled = this.busy || n >= this.limit || !p.available || count >= p.max; b.setAttribute('aria-disabled', String(n >= this.limit || !p.available || count >= p.max)); });
        if (!live || before === count) return;
        if (before === 0) this.animate(stepper, [{opacity:0,transform:'scale(.92)'},{opacity:1,transform:'none'}], 280, 0, SPRING);
        if (count === 0) this.animate(addButton, [{opacity:0,transform:'scale(.94)'},{opacity:1,transform:'none'}], 260);
        if (count > 0) {
          this.animate(badge, [{transform:'scale(0)'},{transform:'scale(1.25)',offset:.6},{transform:'scale(1)'}], 380);
          this.animate(output, [{opacity:0,transform:`translateY(${count > before ? 8 : -8}px)`},{opacity:1,transform:'none'}], 260);
        }
      });
      const first = this.slots[0]?.getBoundingClientRect(), second = this.slots[1]?.getBoundingClientRect();
      const pitch = first && second ? second.left - first.left : 0;
      this.slots.forEach((slot,i) => {
        const p = this.byId.get(this.selected[i]), id = p ? String(p.id) : '';
        slot.classList.toggle('is-next', i === n && n < this.limit);
        if (slot.dataset.id === id) return;
        slot.dataset.id = id;
        slot.classList.toggle('apb-slot-empty', !p);
        const figure = slot.querySelector('.apb-slot-figure'), name = slot.querySelector('.apb-slot-name');
        name.style.removeProperty('opacity');
        figure.innerHTML = p ? `<img src="${esc(p.card.querySelector('img')?.currentSrc || p.image)}" alt="" width="400" height="840" decoding="async">` : this.outline + `<span class="apb-slot-numeral">${i+1}</span>`;
        name.textContent = p ? p.title : i < 1 ? 'Choose scent' : this.target ? (i === this.target - 1 ? `${this.cfg.tiers[this.target]}% off` : 'Your pick') : `${this.cfg.tiers[i+1]}% off`;
        name.classList.toggle('is-reward', !p && i >= 1 && (!this.target || i === this.target - 1));
        slot.querySelector('.apb-remove')?.remove();
        slot.insertAdjacentHTML('beforeend', p ? `<button type="button" class="apb-remove" data-apb-action="remove" data-slot="${i}" aria-label="Remove ${esc(p.title)} from slot ${i+1}"><span aria-hidden="true">×</span></button>` : '<span class="apb-remove" aria-hidden="true"></span>');
        if (!live) return;
        const img = figure.querySelector('img'), x = slot.querySelector('button.apb-remove');
        if (p && i === added) {
          const held = [img, name, x];
          if (!this.motion.matches) held.forEach(node => { node.style.opacity = '0'; });
          const settle = () => {
            if (!img.isConnected) return;
            held.forEach(node => node.style.removeProperty('opacity'));
            this.animate(img, [{opacity:0,transform:'translateY(-18px) scale(.9)'},{opacity:1,transform:'translateY(2px) scale(1.02)',offset:.6},{opacity:1,transform:'none'}], 440);
            this.animate(x, [{opacity:0,transform:'scale(0)'},{opacity:1,transform:'scale(1)'}], 300, 180, SPRING);
            this.animate(name, [{opacity:0},{opacity:1}], 260, 120);
          };
          Promise.all([img.decode().catch(() => {}), flight?.done]).then(settle, settle);
        } else if (p && removed > -1 && i >= removed && pitch) {
          const slide = [{transform:`translateX(${pitch}px)`},{transform:'none'}];
          [img, name, x].forEach(node => this.animate(node, slide, 340));
        } else if (!p) {
          const delay = cleared ? (4 - i) * 45 : 0;
          this.animate(figure, [{opacity:0,transform:'scale(.94)'},{opacity:1,transform:'none'}], 300, delay);
          this.animate(name, [{opacity:0},{opacity:1}], 300, delay);
        }
      });
      this.querySelectorAll('[data-apb-count]').forEach(el => {
        const text = `${n} / ${this.limit}`;
        if (el.textContent === text) return;
        el.textContent = text;
        if (live && !(flight?.toDock && this.dock.contains(el))) this.animate(el, [{opacity:.3,transform:'translateY(-5px)'},{opacity:1,transform:'none'}], 300);
      });
      this.querySelector('[data-apb-progress]').style.transform=`scaleX(${n/this.limit})`;
      this.querySelector('[data-apb-help]').textContent=n>=this.goal?`${n} perfumes chosen · your set is ready`:this.target?`Choose ${this.target} perfumes. Repeats welcome.`:'Choose 2–5 perfumes. Repeats welcome.';
      this.querySelectorAll('[data-apb-price-label]').forEach(el=>el.textContent=n?`${n} × 1 oz perfume${n===1?'':'s'}`:'Your set total');
      this.querySelectorAll('[data-apb-price]').forEach(el=>this.tween(el, price.total, v => this.money(v), tierUp ? price.subtotal : undefined));
      this.querySelectorAll('[data-apb-compare]').forEach(el=>{el.hidden=!price.saving;this.tween(el, price.subtotal, v => this.money(v));});
      this.querySelectorAll('[data-apb-saving]').forEach(el=>{
        if (price.saving) { this.tween(el, price.saving, v => `You save ${this.money(v)} · ${price.percent}% off`); if (tierUp) this.animate(el, [{transform:'scale(1)'},{transform:'scale(1.06)',offset:.4},{transform:'scale(1)'}], 420); }
        else this.setText(el, 'Savings start with 2 perfumes', 0);
      });
      this.querySelectorAll('[data-apb-tier]').forEach(el=>{
        const tier=Number(el.dataset.apbTier);
        el.classList.toggle('is-active',tier===n);
        el.classList.toggle('is-target',tier===this.target);
        if(this.target)el.setAttribute('aria-pressed',String(tier===this.target));
        el.classList.toggle('is-earned',tier<n);
        el.classList.toggle('is-next',n<5 && tier===Math.max(2,n+1));
        if(tier===n)el.setAttribute('aria-current','step');else el.removeAttribute('aria-current');
      });
      this.renderNudge(nudge, n);
      this.dock.querySelectorAll('.apb-dock-meter i').forEach((seg,i) => {
        const on = i < n, wasOn = seg.classList.contains('is-on') || seg.classList.contains('is-best');
        seg.classList.toggle('is-on', on && n < this.limit);
        seg.classList.toggle('is-best', n >= this.limit);
        seg.classList.toggle('is-next', i === n);
        if (live && on && !wasOn) this.animate(seg, [{transform:'scaleX(.2)'},{transform:'none'}], 380, 0, SPRING);
      });
      if (!tierUp && this.dockFlash) this.endDockFlash();
      this.renderDock(nudge);
      this.querySelectorAll('[data-apb-action=minus],[data-apb-action=remove]').forEach(el=>el.disabled=this.busy);
      this.querySelectorAll('[data-apb-action=clear]').forEach(el=>el.disabled=this.busy || n===0);
      this.querySelectorAll('[data-apb-action=cart]').forEach(el=>{
        const ready = !this.busy && this.cfg.available && n >= this.goal;
        el.setAttribute('aria-disabled',String(this.busy || !this.cfg.available));
        el.disabled=this.busy || !this.cfg.available;
        el.classList.toggle('is-incomplete',n<this.goal);
        el.setAttribute('aria-busy',String(this.busy));
        if (ready) this.tween(el, price.total, v => `${this.cfg.ctaLabel || 'Buy now'} · ${this.money(v)}`, tierUp ? price.subtotal : undefined);
        else this.setText(el, this.busy?(this.cfg.afterAdd==='checkout'?'Going to checkout…':'Adding your set…'):!this.cfg.available?'Set currently unavailable':this.target?`Choose ${this.target-n} more`:n===1?'Choose 1 more perfume':'Choose your first 2');
        if (ready && (becameReady || tierUp)) this.flashClass(el, 'is-shine', 1200);
      });
      if (tierUp) this.celebrate(n, price, nudge);
      if (this.dialog.open && this.view === 'review') this.renderReview();
      if (this.dialog.open && this.view === 'detail') this.updateDetail();
      this.prevN = n;
      this.prevPercent = price.percent;
      this.write('selection',this.selected);
      return {tierUp, price};
    }
    renderNudge(nudge, n) {
      const box = this.querySelector('[data-apb-nudge]');
      if (!box) return;
      box.dataset.state = nudge.state;
      box.querySelector('[data-apb-ring]').style.strokeDashoffset = String(100 - Math.min(n,this.limit) * 100 / this.limit);
      const value = box.querySelector('[data-apb-nudge-value]');
      if (value.textContent !== nudge.badge) { value.textContent = nudge.badge; if (!this.first) this.animate(value, [{opacity:0,transform:'scale(.5)'},{opacity:1,transform:'scale(1)'}], 360, 60, SPRING); }
      const copy = box.querySelector('[data-apb-nudge-copy]'), key = nudge.title + nudge.detail;
      if (copy.apbKey === key) return;
      copy.apbKey = key;
      box.querySelector('[data-apb-upsell-title]').textContent = nudge.title;
      box.querySelector('[data-apb-upsell-detail]').textContent = nudge.detail;
      if (!this.first) this.animate(copy, [{opacity:0,transform:'translateY(8px)'},{opacity:1,transform:'none'}], 340);
    }
    renderDock(nudge) {
      const text = this.dock.querySelector('[data-apb-dock-text]');
      if (!text) return;
      const html = this.dockFlash ? this.dockFlash.html : nudge.dock;
      if (text.apbHtml === html) return;
      text.apbHtml = html;
      text.innerHTML = html;
      if (!this.first) this.animate(text, [{opacity:0,transform:'translateY(8px)'},{opacity:1,transform:'none'}], 320);
    }
    endDockFlash() {
      clearTimeout(this.dockTimer);
      this.dockFlash = null;
      this.dock.querySelector('[data-apb-dock-nudge]')?.classList.remove('is-flash');
    }
    celebrate(n, price, nudge) {
      const label = n >= 5 ? '✓ Best value unlocked' : `✓ ${price.percent}% off unlocked`;
      this.endDockFlash();
      this.dockFlash = {html:`<b>${label}</b><span class="apb-dock-extra"> · you save ${this.money(price.saving)}</span>`};
      const strip = this.dock.querySelector('[data-apb-dock-nudge]');
      strip?.classList.add('is-flash');
      this.renderDock(nudge);
      this.dockTimer = setTimeout(() => { this.dockFlash = null; strip?.classList.remove('is-flash'); this.renderDock(this.nudge()); }, 2200);
      if (this.motion.matches) return;
      const big = n >= 5;
      const tile = this.querySelector(`[data-apb-tier="${n}"]`);
      if (tile) { this.animate(tile, [{transform:'scale(1)'},{transform:'scale(1.08)',offset:.35},{transform:'scale(1)'}], 520); this.burst(tile, big ? 14 : 9, big ? 34 : 28); }
      const box = this.querySelector('[data-apb-nudge]');
      if (box) { this.flashClass(box, 'is-celebrating', 1150); this.burst(box.querySelector('.apb-nudge-badge'), big ? 12 : 8, 24); }
      if (strip) this.flashClass(strip, 'is-celebrating', 1150);
    }
    burst(host, count, distance) {
      if (!host || this.motion.matches) return;
      const burst = document.createElement('span');
      burst.className = 'apb-burst'; burst.setAttribute('aria-hidden','true');
      for (let i = 0; i < count; i++) {
        const leaf = document.createElement('i');
        leaf.style.setProperty('--a', `${Math.round(360 / count * i + Math.random() * 20 - 10)}deg`);
        leaf.style.setProperty('--d', `${Math.round(distance * (.75 + Math.random() * .5))}px`);
        leaf.style.setProperty('--t', `${(Math.random() * .08).toFixed(2)}s`);
        burst.append(leaf);
      }
      host.append(burst);
      this.later(() => burst.remove(), 950);
    }
    flashClass(el, name, ms) {
      if (!el || this.motion.matches) return;
      clearTimeout(el.apbFlash);
      el.classList.remove(name);
      void el.offsetWidth;
      el.classList.add(name);
      el.apbFlash = this.later(() => el.classList.remove(name), ms);
    }
    tween(el, to, format, from) {
      cancelAnimationFrame(el.apbRaf);
      const start = from ?? el.apbValue;
      el.apbValue = to;
      if (this.first || this.motion.matches || start === undefined || start === to) { el.textContent = format(to); return; }
      const unit = to % 100 === 0 && start % 100 === 0 ? 100 : 1, began = performance.now(), duration = 520;
      const step = now => {
        const t = Math.min(1, (now - began) / duration), e = 1 - Math.pow(1 - t, 3);
        el.textContent = format(t === 1 ? to : Math.round((start + (to - start) * e) / unit) * unit);
        if (t < 1) el.apbRaf = requestAnimationFrame(step);
      };
      el.textContent = format(start);
      el.apbRaf = requestAnimationFrame(step);
    }
    setText(el, text, value) {
      cancelAnimationFrame(el.apbRaf);
      el.apbValue = value;
      el.textContent = text;
    }
    animate(node, frames, duration, delay = 0, easing = EASE) {
      if (!node || this.motion.matches || !node.animate) return null;
      const animation = node.animate(frames,{duration,delay,easing,fill:delay ? 'backwards' : 'none'});
      this.animations.add(animation);
      animation.finished.catch(() => {}).finally(() => this.animations.delete(animation));
      return animation;
    }
    fly(p, slotIndex) {
      if (this.motion.matches || this.flyers.size > 3 || !document.body.animate) return null;
      const source = p.card.querySelector('.apb-media img');
      const s = source?.getBoundingClientRect();
      if (!s || !s.width || s.bottom < 0 || s.top > innerHeight) return null;
      const dockOn = !this.dock.hidden && getComputedStyle(this.dock).display !== 'none';
      const floor = dockOn ? this.dock.getBoundingClientRect().top - 4 : innerHeight;
      let target = this.slots[slotIndex]?.querySelector('.apb-slot-figure'), t = target?.getBoundingClientRect(), toDock = false;
      if (!t || !t.width || t.top < 0 || t.bottom > floor) {
        if (!dockOn) return null;
        target = this.dock.querySelector('.apb-review'); t = target.getBoundingClientRect(); toDock = true;
      }
      const wrap = document.createElement('div'), img = new Image();
      wrap.className = 'apb-flyer'; wrap.setAttribute('aria-hidden','true');
      Object.assign(wrap.style, {left:`${s.left}px`, top:`${s.top}px`, width:`${s.width}px`, height:`${s.height}px`});
      img.alt = ''; img.src = source.currentSrc || source.src;
      wrap.append(img); (this.closest('dialog') || document.body).append(wrap); this.flyers.add(wrap);
      const dx = t.left + t.width / 2 - (s.left + s.width / 2), dy = t.top + t.height / 2 - (s.top + s.height / 2);
      const end = toDock ? .16 : Math.min(1, t.height / s.height * 1.08), lift = Math.min(64, Math.abs(dy) * .2 + 24), duration = 640;
      const across = wrap.animate([{transform:'translateX(0)'},{transform:`translateX(${dx}px)`}], {duration, easing:'cubic-bezier(.3,0,.2,1)', fill:'forwards'});
      const arc = img.animate([
        {transform:'translateY(0) scale(1)', opacity:1, easing:'cubic-bezier(.2,.7,.4,1)'},
        {transform:`translateY(${-lift}px) scale(${((1 + end) / 1.7).toFixed(3)})`, opacity:1, offset:.3, easing:'cubic-bezier(.55,0,.8,.3)'},
        {transform:`translateY(${dy}px) scale(${end})`, opacity:toDock ? .35 : 1}
      ], {duration, fill:'forwards'});
      const done = Promise.all([across.finished, arc.finished]).catch(() => {}).then(() => {
        wrap.remove(); this.flyers.delete(wrap);
        if (toDock) this.animate(target, [{transform:'scale(1)'},{transform:'scale(1.14)',offset:.4},{transform:'scale(1)'}], 380);
      });
      return {done, toDock};
    }
    toggleDock(show) {
      if (show && this.dock.hidden) {
        this.dock.hidden = false;
        this.measureDock();
        this.animate(this.dock, [{translate:'0 130%',opacity:0},{translate:'0 0',opacity:1}], 480);
      } else if (!show) this.dock.hidden = true;
    }
    measureDock() { if (!this.dock.hidden && this.dock.offsetHeight) this.style.setProperty('--apb-dock-h', `${this.dock.offsetHeight}px`); }
    filter(family) {
      let visible = 0;
      const shown = [];
      this.cards.forEach((card,i) => { card.hidden = !!family && this.products[i].family !== family; if (!card.hidden) { visible++; shown.push(card); } });
      shown.slice(0,9).forEach((card,i) => this.animate(card, [{opacity:0,transform:'translateY(12px)'},{opacity:1,transform:'none'}], 360, i * 40));
      this.announce(`${visible} scents shown.`);
    }
    announce(message) { this.querySelector('[data-apb-live]').textContent = message; }
    toast(message) {
      const el=this.querySelector('[data-apb-toast]');
      clearTimeout(this.toastTimer);
      el.classList.remove('is-leaving');
      if (!el.hidden) { el.hidden = true; void el.offsetWidth; }
      el.textContent=message; el.hidden=false;
      this.announce(message);
      this.toastTimer=setTimeout(()=>{
        if (this.motion.matches) { el.hidden = true; return; }
        el.classList.add('is-leaving');
        this.toastTimer=setTimeout(()=>{ el.hidden=true; el.classList.remove('is-leaving'); },240);
      },3400);
    }
    open(view,id,trigger) {
      clearTimeout(this.closeTimer);
      if (this.dialog.classList.contains('is-closing')) { this.dialog.classList.remove('is-closing'); if (this.dialog.open) this.dialog.close(); }
      this.view=view;this.detailId=String(id);this.returnFocus=trigger;
      this.querySelector('[data-apb-dialog-title]').textContent = view === 'detail' ? 'Scent details' : 'Review your set';
      if (view === 'detail') this.renderDetail(); else this.renderReview();
      this.oldOverflow=document.body.style.overflow;document.body.style.overflow='hidden';
      this.dialog.showModal();
      if (view === 'detail') this.fitIngredients();
      this.dialog.querySelector('[data-apb-action=close]').focus();
      if (view === 'review') this.dialog.querySelectorAll('.apb-review-list li').forEach((li,i) => this.animate(li, [{opacity:0,transform:'translateY(10px)'},{opacity:1,transform:'none'}], 340, 140 + i * 50));
      else this.animate(this.dialog.querySelector('.apb-detail-media img'), [{opacity:0,transform:'translateY(14px)'},{opacity:1,transform:'none'}], 480, 120);
    }
    closeDialog() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      if (this.motion.matches) { this.dialog.close(); return; }
      this.dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(() => { this.dialog.classList.remove('is-closing'); if (this.dialog.open) this.dialog.close(); }, 270);
    }
    closed() {
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      if (this.oldOverflow!==undefined) {document.body.style.overflow=this.oldOverflow;this.oldOverflow=undefined;}
      this.returnFocus?.focus({preventScroll:true});
    }
    ingredients(p) {
      const raw = String(p.keyIngredients || p.ingredients || '').trim();
      if (!raw) return {items:[], note:''};
      let note = '';
      const lines = raw.split(/\n+/).map(line => line.trim()).filter(Boolean).filter(line => {
        if (/^(ingredient lists|please)/i.test(line)) { note = line; return false; }
        return true;
      });
      const sizeOnly = /\s*\((?:[\d.]+\s*oz\s+)?(spray|roller)s?\s+only\)\s*$/i;
      const items = (lines.length > 1 ? lines : lines.join(', ').split(/\s*[,·]\s*/))
        .map(item => item.replace(/\.$/, '').trim()).filter(Boolean)
        .filter(item => { const m = item.match(sizeOnly); return !m || m[1].toLowerCase() === 'spray'; })
        .map(item => item.replace(sizeOnly, ''));
      return {items, note};
    }
    fitIngredients() {
      // Hide "Show all" when the whole list already fits on one line
      const box = this.querySelector('[data-apb-ingredients]');
      if (!box) return;
      const preview = box.querySelector('[data-apb-ingr-preview]'), toggle = box.querySelector('[data-apb-action=ingredients]');
      const fits = preview.scrollWidth <= preview.clientWidth + 1 && !box.querySelector('.apb-ingr-note');
      toggle.hidden = fits;
      box.classList.toggle('is-short', fits);
    }
    toggleIngredients(button) {
      const box = button.closest('[data-apb-ingredients]');
      if (!box) return;
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      button.querySelector('[data-apb-ingr-label]').textContent = open ? 'Show less' : 'Show all';
      box.classList.toggle('is-open', open);
      box.querySelectorAll('[data-apb-ingr-full]').forEach(el => { el.hidden = !open; });
      box.querySelector('[data-apb-ingr-preview]').hidden = open;
      if (open) this.animate(box.querySelector('.apb-ingr-list'), [{opacity:0,transform:'translateY(-4px)'},{opacity:1,transform:'none'}], 260);
    }
    renderDetail() {
      const p=this.byId.get(this.detailId);if(!p)return;
      const small = matchMedia('(max-width: 1023px)').matches;
      const link = `${p.url}${String(p.url).includes('?') ? '&' : '?'}variant=${p.id}`;
      const notes = [['Top', p.top], ['Middle', p.middle], ['Base', p.base]].filter(([, value]) => value && String(value).trim());
      const ingr = this.ingredients(p);
      const specs = [];
      if (this.cfg.longevity) specs.push(`<div class="apb-spec"><dt>Longevity</dt><dd>${esc(this.cfg.longevity)}</dd></div>`);
      if (ingr.items.length) specs.push(`<div class="apb-spec apb-spec--ingr" data-apb-ingredients><dt>Key ingredients</dt><dd><p class="apb-ingr-preview" data-apb-ingr-preview>${esc(ingr.items.join(', '))}</p><ul class="apb-ingr-list" data-apb-ingr-full hidden>${ingr.items.map(item => `<li>${esc(item)}</li>`).join('')}</ul>${ingr.note ? `<p class="apb-ingr-note" data-apb-ingr-full hidden>${esc(ingr.note)}</p>` : ''}<button type="button" class="apb-ingr-toggle" data-apb-action="ingredients" aria-expanded="false"><span data-apb-ingr-label>Show all</span><svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="m3 4.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button></dd></div>`);
      const rail = notes.length
        ? `<section class="apb-notes" aria-labelledby="${this.id}-notes-title"><h4 class="apb-notes-title" id="${this.id}-notes-title">Scent notes</h4><ol class="apb-notes-rail">${notes.map(([label, value]) => `<li class="apb-note"><span class="apb-note-dot" aria-hidden="true"></span><span class="apb-note-label">${label}</span><span class="apb-note-value">${esc(value)}</span></li>`).join('')}</ol></section>`
        : (p.notes ? `<p class="apb-detail-notes">${esc(p.notes)}</p>` : '');
      const external = small ? '' : ' target="_blank" rel="noopener"';
      const linkText = small ? 'View full perfume details' : 'View this perfume in a new tab';
      const linkIcon = small ? '' : '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M9.5 2.5h4v4M13.5 2.5 7.5 8.5M12 9.5v3.5a.5.5 0 0 1-.5.5h-8.5a.5.5 0 0 1-.5-.5v-8.5a.5.5 0 0 1 .5-.5h3.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="apb-sr"> (opens in a new tab)</span>';
      this.querySelector('[data-apb-dialog-body]').innerHTML = `<div class="apb-detail-head"><div class="apb-detail-intro"><p class="apb-detail-family">${esc(p.family)}</p><h3 class="apb-detail-title">${esc(p.title)}</h3><p class="apb-detail-meta">1 oz perfume spray · ${this.money(p.price)} separately</p></div><div class="apb-detail-media"><img src="${esc(p.image)}" alt="${esc(p.title)} 1 oz perfume spray" width="400" height="840"></div></div>${rail}${specs.length ? `<dl class="apb-specs">${specs.join('')}</dl>` : ''}<div class="apb-detail-actions"><p class="apb-detail-status" data-apb-detail-status></p><button type="button" class="apb-button" data-apb-action="add" data-id="${p.id}" data-apb-detail-add></button><a class="apb-detail-link" href="${esc(link)}"${external}>${linkText}${linkIcon}</a></div>`;
      this.updateDetail();
    }
    updateDetail() {
      const p=this.byId.get(this.detailId), b=this.querySelector('[data-apb-detail-add]');if(!p||!b)return;
      const count=this.quantity(p.id);b.textContent=!p.available?'Currently unavailable':this.selected.length>=this.limit?'Your set is full':count?`Add another ${p.title}`:`Add ${p.title}`;
      b.disabled=this.busy || this.selected.length>=this.limit || !p.available || count>=p.max;b.setAttribute('aria-disabled',String(this.selected.length>=this.limit || b.disabled));
      this.querySelector('[data-apb-detail-status]').textContent=`${count} in your set · ${this.selected.length} of ${this.limit} chosen`;
    }
    renderReview() {
      const price=this.pricing(),nudge=this.nudge(price),body=this.querySelector('[data-apb-dialog-body]');
      body.innerHTML=`${this.selected.length?`<div class="apb-review-toolbar"><button type="button" class="apb-clear" data-apb-action="clear" ${this.busy?'disabled':''}>Clear set</button></div>`:''}${this.selected.length?`<ol class="apb-review-list">${this.selected.map((id,i)=>{const p=this.byId.get(id);return `<li><img src="${esc(p.image)}" alt="" width="400" height="840"><span>${esc(p.title)}<small>Perfume ${i+1} · 1 oz</small></span><button type="button" data-apb-action="remove" data-slot="${i}" aria-label="Remove ${esc(p.title)} from slot ${i+1}" ${this.busy?'disabled':''}>×</button></li>`;}).join('')}</ol>`:'<p>Choose 2–5 perfumes. Repeat the scents you love.</p>'}<div class="apb-price-row"><span class="apb-price-label">${price.n?`Your set of ${price.n}`:'Your set total'}</span><div class="apb-prices"><s ${price.saving?'':'hidden'}>${this.money(price.subtotal)}</s><strong>${this.money(price.total)}</strong></div></div><p class="apb-review-saving">${price.saving?`You save ${this.money(price.saving)} · ${price.percent}% off`:'Savings start with 2 perfumes'}</p><button type="button" class="apb-button" data-apb-action="${price.n>=this.goal?'cart':'close'}" ${this.busy || (price.n>=this.goal && !this.cfg.available)?'disabled':''} aria-busy="${this.busy}">${this.busy?(this.cfg.afterAdd==='checkout'?'Going to checkout…':'Adding your set…'):price.n>=this.goal?`${this.cfg.ctaLabel || 'Buy now'} · ${this.money(price.total)}`:'Keep choosing'}</button><p class="apb-tier-note">${esc(nudge.title)}</p><p class="apb-message" data-apb-dialog-message role="status">${esc(this.lastError || '')}</p>`;
    }
    async request(url, options={}) {
      const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),15000);
      try { const r=await fetch(url,{credentials:'same-origin',...options,signal:controller.signal});const data=await r.json();if(!r.ok){const error=new Error(data.description || data.message || 'Please try again.');error.status=r.status;throw error;}return data; }
      finally {clearTimeout(timer);}
    }
    pendingStatus(cart,pending) {
      const lines=(cart.items || []).filter(item=>item.properties?._adk_request===pending.token);
      const complete=pending.items.every(expected=>lines.filter(item=>String(item.variant_id)===String(expected.id)).reduce((sum,item)=>sum+item.quantity,0)===expected.quantity);
      return {complete,partial:lines.length>0,total:lines.reduce((sum,item)=>sum+(item.final_line_price??item.line_price),0)};
    }
    error(message,cartLink=false) {
      this.lastError=message;const box=this.querySelector('[data-apb-error]');box.textContent=message;box.hidden=false;
      if(cartLink){const link=document.createElement('a');link.href=this.cfg.cartUrl || '/cart';link.textContent=' View your cart';link.addEventListener('click',event=>{if(this.openCartDrawer())event.preventDefault();});box.append(link);}
      const target=this.querySelector('[data-apb-dialog-message]');if(target)target.textContent=message;
      this.toast(message);
    }
    confirmCart(cart,pending) {
      const status=this.pendingStatus(cart,pending);
      if(status.complete && status.total<=pending.expectedTotal){this.success(cart);return true;}
      if(status.complete){this.error('Your perfumes are in your cart, but the expected set discount was not applied. Review your cart before checking out.',true);return true;}
      if(status.partial){this.error('Only part of your set is in the cart. Please review it before adding more perfumes.',true);return true;}
      return false;
    }
    async submit() {
      if(this.busy)return;
      if(this.selected.length<this.goal){
        if(this.dialog.open)this.closeDialog();
        this.querySelector('.apb-toolbar').scrollIntoView({behavior:this.motion.matches?'instant':'smooth',block:'start'});
        this.querySelector('[data-apb-filter]').focus({preventScroll:true});
        this.announce(`Choose ${this.goal-this.selected.length} more perfume${this.goal-this.selected.length===1?'':'s'} to ${this.target?'complete':'start'} your set.`);return;
      }
      if(!this.cfg.available){this.error('This set is currently unavailable. Please check back soon.');return;}
      if(this.cfg.preview){
        if(!this.dialog.open)this.open('review',null,this.querySelector('[data-apb-tray-cta]'));
        this.querySelector('[data-apb-dialog-title]').textContent='Your set is ready';
        this.querySelector('[data-apb-dialog-message]').textContent=`Preview only: ${this.selected.length} perfumes, ${this.cfg.tiers[this.selected.length]}% off. No live cart or order was created.`;
        this.announce('Your preview set is ready.');return;
      }
      this.lastError='';this.busy=true;this.querySelector('[data-apb-error]').hidden=true;this.update();
      const root=this.cfg.root || '/';let posted=false;
      try {
        if(this.pending){
          const cart=await this.request(`${root}cart.js`);
          if(this.confirmCart(cart,this.pending))return;
          this.pending=null;this.write('pending',null);
        }
        const unique=[...new Set(this.selected)];
        const sources=await Promise.all(unique.map(id=>this.request(`${this.byId.get(id).url}.js`)));
        let changed=false;
        for(let i=0;i<unique.length;i++){
          const p=this.byId.get(unique[i]),v=sources[i].variants.find(v=>String(v.id)===unique[i]);
          if(!v?.available){p.available=false;throw new Error(`${p.title} is currently unavailable. Remove it and choose another scent.`);}
          if(p.price!==v.price){p.price=v.price;changed=true;}
        }
        if(changed){this.error('A perfume price has changed. Review your updated total, then add your set.');return;}
        const token=globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const items=unique.map(id=>({id:Number(id),quantity:this.quantity(id),properties:{_adk_request:token,'Perfume set':`Build your own · ${this.selected.length} perfumes`}}));
        this.pending={token,items:items.map(({id,quantity})=>({id,quantity})),expectedTotal:this.pricing().total};this.write('pending',this.pending);
        posted=true;
        await this.request(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const cart=await this.request(`${root}cart.js`);
        if(!this.confirmCart(cart,this.pending))throw new Error('We could not confirm your perfumes in the cart.');
      }catch(error){
        if(posted){
          try{const cart=await this.request(`${root}cart.js`);if(this.confirmCart(cart,this.pending))return;}
          catch{this.error('We could not confirm the cart. Open it before trying again. Your choices are saved.',true);return;}
          this.pending=null;this.write('pending',null);
        }
        this.error(error.name==='AbortError'?'The connection timed out. Your choices are saved; please try again.':error.message || 'We could not add your set. Please try again.');
      }finally{this.busy=false;this.update();}
    }
    openCartDrawer() {
      // Monster Cart: refresh the drawer with the new lines, then open it on this page.
      if (this.cfg.afterAdd === 'page') return false;
      const refresh = typeof window.monster_refresh === 'function' ? window.monster_refresh : null;
      const open = typeof window.openCart === 'function' ? window.openCart : null;
      if (!refresh && !open) return false;
      try { if (refresh) refresh(); if (open) open(); return true; }
      catch (error) { return false; }
    }
    success(result) {
      this.pending=null;this.write('pending',null);this.selected=[];this.write('selection',[]);
      this.dispatchEvent(new CustomEvent('adk:perfume-set-added',{bubbles:true,detail:{cart:result}}));
      if (this.dialog.open) { clearTimeout(this.closeTimer); this.dialog.classList.remove('is-closing'); this.dialog.close(); }
      if (this.cfg.afterAdd === 'checkout') { this.announce('Taking you to checkout.'); window.location.assign(`${this.cfg.root || '/'}checkout`); return; }
      if (this.openCartDrawer()) { this.announce('Your set is in your cart.'); return; }
      window.location.assign(this.cfg.cartUrl || `${this.cfg.root || '/'}cart`);
    }
  }
  customElements.define('adk-perfume-set',ADKPerfumeSet);
})();
